import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { API_ENDPOINTS } from "config/api";

// @mui material components
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from '@mui/material/CircularProgress';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const { logout } = useAuth();

    const [formData, setFormData] = useState({
        newPassword: "",
        confirmPassword: "",
    });
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [isTokenValid, setIsTokenValid] = useState(true); // Default to true until verified
    const [message, setMessage] = useState({ type: "", text: "" });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);

    // Helper to decode Base64 encoded JWT payload (robust)
    const getExpFromToken = (tokenToParse) => {
        try {
            if (!tokenToParse) return null;

            let jwtString = tokenToParse;

            // Check if it's a raw JWT (has 2 dots)
            if (tokenToParse.split('.').length === 3) {
                jwtString = tokenToParse;
            } else {
                // Assume it's Base64 encoded
                try {
                    jwtString = atob(tokenToParse);
                } catch (e) {
                    console.error("Failed to base64 decode token:", e);
                    return null;
                }
            }

            // Now parse the JWT part
            const parts = jwtString.split('.');
            if (parts.length !== 3) return null;

            const base64Url = parts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            const payload = JSON.parse(jsonPayload);
            return payload.exp;
        } catch (e) {
            console.error("Error decoding token exp:", e);
            return null;
        }
    };

    useEffect(() => {
        let timerInterval;

        const verifyToken = async () => {
            if (!token) {
                setIsTokenValid(false);
                setVerifying(false);
                return;
            }

            // Try to extract exp directly from token first (fallback for backend update lag)
            let expirationTime = getExpFromToken(token);

            try {
                const response = await fetch(API_ENDPOINTS.VERIFY_RESET_TOKEN, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ token }),
                });

                const result = await response.json();

                if (result.success) {
                    setIsTokenValid(true);

                    // Backend exp takes precedence if available
                    if (result.exp) {
                        expirationTime = result.exp;
                    }

                    // Set timer if we have an expiration time
                    if (expirationTime) {
                        const calculateTimeLeft = () => {
                            const currentTime = Math.floor(Date.now() / 1000);
                            const remaining = expirationTime - currentTime;
                            return remaining > 0 ? remaining : 0;
                        };

                        const initialTimeLeft = calculateTimeLeft();
                        setTimeLeft(initialTimeLeft);

                        if (initialTimeLeft > 0) {
                            timerInterval = setInterval(() => {
                                const remaining = calculateTimeLeft();
                                setTimeLeft(remaining);

                                if (remaining <= 0) {
                                    clearInterval(timerInterval);
                                    setIsTokenValid(false);
                                }
                            }, 1000);
                        } else {
                            // Already expired
                            setIsTokenValid(false);
                        }
                    }

                } else {
                    setIsTokenValid(false);
                }
            } catch (error) {
                console.error("Token verification error:", error);
                setIsTokenValid(false);
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();

        return () => {
            if (timerInterval) clearInterval(timerInterval);
        };
    }, [token]);




    const handleInputChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value,
        });
    };

    const handleToggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!isTokenValid) return;

        if (!formData.newPassword || !formData.confirmPassword) {
            setMessage({ type: "error", text: "Both password fields are required!" });
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setMessage({ type: "error", text: "Passwords do not match!" });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    token: token,
                    newPassword: formData.newPassword,
                }),
            });

            const result = await response.json();

            if (result.success) {
                setMessage({
                    type: "success",
                    text: "Password reset successfully! Redirecting to sign-in...",
                });

                setTimeout(() => {
                    logout();
                    navigate("/authentication/sign-in", { replace: true });
                }, 3000);
            } else {
                setMessage({ type: "error", text: result.message });
            }
        } catch (error) {
            setMessage({ type: "error", text: "Password reset failed. Please validate your token or try again." });
            console.error("Password reset error:", error);
        }
        setLoading(false);
    };

    // Show loading state while verifying
    if (verifying) {
        return (
            <MDBox
                sx={{
                    minHeight: "100vh",
                    width: "100vw",
                    background: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress color="info" />
            </MDBox>
        );
    }

    if (!isTokenValid) {
        return (
            <MDBox
                sx={{
                    minHeight: "100vh",
                    width: "100vw",
                    background: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                }}
            >
                <Container maxWidth="sm">
                    <Card
                        sx={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                            border: "1px solid #e2e8f0",
                            maxWidth: "400px",
                            margin: "0 auto",
                            textAlign: "center",
                            p: 4
                        }}
                    >
                        <MDBox mb={2}>
                            <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ mb: 4 }}>
                                Reset password
                            </MDTypography>
                        </MDBox>

                        <MDBox display="flex" justifyContent="center" mb={2}>
                            <MDBox
                                sx={{
                                    width: "80px",
                                    height: "80px",
                                    borderRadius: "50%",
                                    border: "2px solid #ef4444", // Red border
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CancelOutlinedIcon sx={{ fontSize: "50px", color: "#ef4444" }} />
                            </MDBox>
                        </MDBox>

                        <MDTypography variant="h5" fontWeight="bold" color="text" sx={{ mb: 1 }}>
                            Link Expired
                        </MDTypography>

                        <MDTypography variant="body2" color="text" sx={{ mb: 0 }}>
                            To reset your password, return to the{" "}
                            <Link
                                to="/authentication/sign-in"
                                style={{
                                    color: "#1f93ff",
                                    textDecoration: "none",
                                    fontWeight: "500"
                                }}
                            >
                                login
                            </Link>
                        </MDTypography>
                        <MDTypography variant="body2" color="text">
                            <Link
                                to="/authentication/sign-in"
                                style={{
                                    color: "#1f93ff",
                                    textDecoration: "none",
                                    fontWeight: "500"
                                }}
                            >
                                page
                            </Link>
                        </MDTypography>
                    </Card>
                </Container>
            </MDBox>
        );
    }

    return (
        <MDBox
            sx={{
                minHeight: "100vh",
                width: "100vw",
                background: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
            }}
        >
            <Container maxWidth="sm">
                <Card
                    sx={{
                        background: "#ffffff",
                        borderRadius: "12px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        border: "1px solid #e2e8f0",
                        maxWidth: "400px",
                        margin: "0 auto",
                    }}
                >
                    <MDBox p={4}>
                        {/* Header */}
                        <MDBox textAlign="left" mb={4}>
                            <MDTypography
                                variant="h4"
                                sx={{
                                    color: "#1f2937",
                                    fontWeight: "600",
                                    fontSize: "1.875rem",
                                    mb: 1
                                }}
                            >
                                Reset Password
                            </MDTypography>

                            <MDTypography
                                variant="body2"
                                sx={{
                                    color: "#6b7280",
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                }}
                            >
                                Enter your new password below
                            </MDTypography>
                        </MDBox>

                        {message.text && (
                            <MDBox mb={3}>
                                <Alert severity={message.type}>{message.text}</Alert>
                            </MDBox>
                        )}

                        {/* Form */}
                        <MDBox component="form" onSubmit={handleSubmit}>
                            <MDBox mb={3}>
                                <MDTypography
                                    variant="body2"
                                    sx={{ color: "#374151", fontWeight: "500", mb: 1, fontSize: "0.875rem" }}
                                >
                                    New Password
                                </MDTypography>
                                <TextField
                                    type={showNewPassword ? "text" : "password"}
                                    placeholder="Enter new password"
                                    fullWidth
                                    value={formData.newPassword}
                                    onChange={handleInputChange("newPassword")}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleToggleNewPasswordVisibility}
                                                    edge="end"
                                                    sx={{ color: "#6b7280", padding: "8px", marginRight: "4px" }}
                                                >
                                                    {showNewPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "6px",
                                            border: "1px solid #d1d5db",
                                            backgroundColor: "#ffffff",
                                            fontSize: "0.875rem",
                                            "& fieldset": { border: "none" },
                                            "&:hover": { borderColor: "#9ca3af" },
                                            "&:hover fieldset": { border: "none" },
                                            "&.Mui-focused": { borderColor: "#1f93ff" },
                                            "&.Mui-focused fieldset": { border: "none" },
                                        },
                                        "& .MuiInputBase-input": {
                                            padding: "12px 16px",
                                            fontSize: "0.875rem",
                                            color: "#374151",
                                            "&::placeholder": { color: "#9ca3af", opacity: 1 },
                                        },
                                    }}
                                />
                            </MDBox>

                            <MDBox mb={4}>
                                <MDTypography
                                    variant="body2"
                                    sx={{ color: "#374151", fontWeight: "500", mb: 1, fontSize: "0.875rem" }}
                                >
                                    Confirm Password
                                </MDTypography>
                                <TextField
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm new password"
                                    fullWidth
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange("confirmPassword")}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleToggleConfirmPasswordVisibility}
                                                    edge="end"
                                                    sx={{ color: "#6b7280", padding: "8px", marginRight: "4px" }}
                                                >
                                                    {showConfirmPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "6px",
                                            border: "1px solid #d1d5db",
                                            backgroundColor: "#ffffff",
                                            fontSize: "0.875rem",
                                            "& fieldset": { border: "none" },
                                            "&:hover": { borderColor: "#9ca3af" },
                                            "&:hover fieldset": { border: "none" },
                                            "&.Mui-focused": { borderColor: "#1f93ff" },
                                            "&.Mui-focused fieldset": { border: "none" },
                                        },
                                        "& .MuiInputBase-input": {
                                            padding: "12px 16px",
                                            fontSize: "0.875rem",
                                            color: "#374151",
                                            "&::placeholder": { color: "#9ca3af", opacity: 1 },
                                        },
                                    }}
                                />
                            </MDBox>

                            <MDBox mb={4}>
                                <MDButton
                                    type="submit"
                                    fullWidth
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        background: "#1f93ff",
                                        borderRadius: "6px",
                                        textTransform: "none",
                                        fontSize: "0.875rem",
                                        fontWeight: "500",
                                        py: 1.5,
                                        color: "#ffffff",
                                        boxShadow: "none",
                                        "&:hover": { background: "#1e40af", boxShadow: "none" },
                                    }}
                                >
                                    {loading ? "Updating..." : "Update Password"}
                                </MDButton>
                            </MDBox>

                            <MDBox textAlign="center">
                                <MDTypography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                    If you want to go back to the login page,{" "}
                                    <Link
                                        to="/authentication/sign-in"
                                        style={{
                                            color: "#1f93ff",
                                            textDecoration: "none",
                                            fontWeight: "500"
                                        }}
                                    >
                                        click here
                                    </Link>
                                    .
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                </Card>
            </Container>
        </MDBox >
    );
}

export default ResetPassword;
